import { layer } from '@layui/layui-vue';
import { Component, h, unref } from 'vue';
import { ANY_OBJECT } from '@/types/generic';
import { getAppId, getToken, getUUID } from '@/common/utils';
import { DialogProp } from './types';
import Layout from './layout.vue';

const LAYER_Z_INDEX = 500;

export class Dialog {
  private static index = 0;

  static closeAll = () => {
    layer.closeAll();
    Dialog.index = 0;
  };

  // 未提供单独关闭某个对话框的方法，涉及到z-index的变化规则，若需提供，须考虑z-index的变化规则
  // options可参考：http://www.layui-vue.com/zh-CN/components/layer 和 https://layui.dev/docs/2/layer/#options

  /**
   * 打开弹窗
   * @param {*} title 弹窗标题
   * @param {*} component 弹窗内容的组件
   * @param {*} options 弹窗设置（详情请见layui官网  http://www.layui-vue.com/zh-CN/components/layer 和 https://layui.dev/docs/2/layer/#options）
   * @param {*} params 弹窗组件参数
   * @param {*} thirdPartyOptions 第三方接入参数
   * @param {*} thirdPartyOptions.pathName 接入路由name
   * @param {*} thirdPartyOptions.width 弹窗宽度
   * @param {*} thirdPartyOptions.height 弹窗高度
   */
  static show = <D>(
    title: string,
    component: Component | string,
    options?: ANY_OBJECT,
    params?: ANY_OBJECT,
    thirdPartyOptions?: ANY_OBJECT,
  ) => {
    // 调用第三方弹窗方法
    if (getAppId() != null && getAppId() !== '') {
      if (thirdPartyOptions && window.parent) {
        showDialog(title, params, thirdPartyOptions);
        return new Promise<D>((resolve, reject) => {
          const eventListener = (e: ANY_OBJECT) => {
            if (e.data.type === 'refreshData') {
              console.log('第三方弹窗关闭后，回传的数据', e);
              window.removeEventListener('message', eventListener);
              resolve(e.data.data?.data as D);
            }
          };
          window.addEventListener('message', eventListener, false);
        });
      } else {
        console.warn('错误的第三方调用！');
        return Promise.reject('错误的第三方调用！');
      }
    }

    return new Promise<D>((resolve, reject) => {
      const observer: DialogProp<D> = {
        index: '',
        cancel: () => {
          if (observer.index && typeof observer.index === 'string') {
            layer.close(observer.index);
          }
          reject({ message: 'canceled' });
        },
        submit: (data: D) => {
          if (observer.index && typeof observer.index === 'string') {
            layer.close(observer.index);
          }
          resolve(data);
        },
      };

      let layerOptions: ANY_OBJECT = {
        title: title,
        type: 1,
        skin:
          'layui-layer-page ' +
          (window.innerWidth <= 1900 ? 'container-default' : 'container-large'),
        resize: false,
        offset: 'auto',
        shadeClose: false,
        content: '' as string | Component,
        zIndex: LAYER_Z_INDEX + Dialog.index,
        end: () => {
          //console.log('layer end');
          Dialog.index--;
        },
      };
      // end之后，要执行index--
      if (options && options.end) {
        const end = options.end;
        layerOptions.end = () => {
          Dialog.index--;
          if (typeof end == 'function') {
            end();
          }
        };
      }

      // 根据fullscreen参数决定显示方式
      // 显式获取fullscreen值并确保正确处理字符串'drawer'
      // 优先使用params中的fullscreen配置，如果params中存在则使用它，否则使用options中的配置
      const fullscreenValue =
        params?.fullscreen !== undefined ? params?.fullscreen : options?.fullscreen;

      // 调试输出，帮助理解fullscreenValue的实际值和类型
      console.log(
        'Dialog show - fullscreenValue:',
        fullscreenValue,
        'type:',
        typeof fullscreenValue,
      );

      // 确保正确处理不同类型的值
      // 如果是全屏模式，设置全屏相关属性，并且为了避免嵌套弹窗
      if (fullscreenValue === true || fullscreenValue === 'true' || fullscreenValue === 1) {
        // 关闭所有现有的弹窗，避免嵌套
        layer.closeAll();
        // 设置全屏属性
        layerOptions.fullScreen = true;
        layerOptions.maxmin = false;
        layerOptions.area = ['100%', '100%'];
        layerOptions.offset = 'auto';
        // 确保没有遮罩，避免视觉上的嵌套感
        layerOptions.shade = 0;
        // 最高层级，确保全屏弹窗在最顶层
        layerOptions.zIndex = 9999;
      }
      // 如果是抽屉模式，我们需要特殊处理
      else if (fullscreenValue === 'drawer') {
        console.log('Drawer mode detected!');
        // 对于抽屉模式，我们使用特殊的样式和配置
        layerOptions.skin = 'layui-layer-drawer';
        layerOptions.area = ['50%', '100%'];
        layerOptions.offset = 'r'; // 右侧滑出
        layerOptions.title = '';
        layerOptions.shadeClose = true;
        // 设置较高层级，但低于全屏模式
        layerOptions.zIndex = 8888;
        // 禁用所有动画效果
        layerOptions.isOutAnim = false;
        layerOptions.isInAnim = false;
        layerOptions.anim = 0; // 设置动画类型为0（无动画）
      }
      // 普通弹窗模式，保持原有逻辑
      else {
        console.log('Normal dialog mode');
        // 可以根据需要设置普通弹窗的默认样式
      }

      layerOptions = { ...layerOptions, ...options };

      params = { ...params };
      params.dialog = observer;

      console.log('dialog params', params);
      const vueComponent = h(Layout, () => h(component, params));
      layerOptions.content = vueComponent;

      const id = layer.open(layerOptions);
      observer.index = id;
      Dialog.index++;
    });
  };
}

function showDialog(title: string, params?: ANY_OBJECT, options?: ANY_OBJECT) {
  console.log('第三方弹窗', title, params, options);
  // 调用第三方弹窗方法
  if (options && window.parent) {
    const paramsCopy: ANY_OBJECT = {};
    if (params) {
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const element = params[key];
          paramsCopy[key] = unref(element);
        }
      }
    }

    const dialogKey = getUUID();
    const location = window.location;
    let dlgUrl =
      location.origin +
      location.pathname +
      '#' +
      options.pathName +
      '?appId=' +
      getAppId() +
      '&token=' +
      getToken() +
      '&dlgFullScreen=' +
      (options.fullscreen === true ? '1' : options.fullscreen === 'drawer' ? '2' : '0') +
      '&dialogKey=' +
      dialogKey;

    dlgUrl += '&thirdParamsString=' + encodeURIComponent(JSON.stringify(paramsCopy));
    const data = {
      title: title,
      dlgFullScreen: options.fullscreen,
      width: options.width,
      height: options.height,
      top: options.top,
      params: paramsCopy,
      url: dlgUrl,
      dialogKey: dialogKey,
    };

    const dlgOption = {
      type: 'openDialog',
      data: JSON.parse(JSON.stringify(data)),
    };
    window.parent.postMessage(dlgOption, '*');
  }
}
